import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { Mutex } from 'async-mutex';
import { browserName, osName, browserVersion } from 'react-device-detect';
import { RootState } from '../../../store/store';
import { IMultiAccount } from '../../../models/UserInfo';
import { LocalStorageItems } from '../../../models/Common';
import { authSlice } from '../../../store/reducers/AuthSlice';
import { xDeviceId } from '../../../constants/configuration';
import { config } from '../config';

const mutex = new Mutex();

const excludeUrlsMultiAccount = [
  config.USER_INFO.INFO,
  config.USER_INFO.BANNERS,
  config.USER_INFO.REPAIR_WORKS,
  config.PUSH_NOTIFICATIONS.GET_NOTIFICATIONS,
  config.USER_INFO.PUSHS_SETTINGS,
  config.USER_INFO.RECOMMENDED_TOP_UP_AMOUNT,
];

const handleUpdateMultiAccountUrl = (args: string | FetchArgs, controlledAccount: IMultiAccount | null) => {
  if (!controlledAccount) return args;

  let url = typeof args === 'string' ? args : args.url;

  if (excludeUrlsMultiAccount.includes(url)) return args;

  const params = new URLSearchParams({
    subAccountId: controlledAccount.UserID.toString(),
  });

  if (url.includes('?')) {
    url += `&${params.toString()}`;
  } else {
    url += `?${params.toString()}`;
  }

  return typeof args === 'string' ? url : { ...args, url };
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND || '',
  prepareHeaders: (headers, { getState }) => {
    try {
      const { accessToken } = (getState() as RootState).authReducer.authData;
      const { selectedLanguage } = (getState() as RootState).langReducer;
      headers.set('x-client-app-device', 'browser');
      headers.set('x-client-app-os', osName);
      headers.set('x-client-app-model', browserName);
      headers.set('x-client-app-version', browserVersion);
      headers.set('X-DEVICE-ID', xDeviceId);
      if (selectedLanguage) {
        headers.set('X-Language', selectedLanguage);
      }

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
    } catch (e: unknown) {
      headers.set('authorization', 'Bearer error');
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  await mutex.waitForUnlock();

  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);

  const updatedArgs = handleUpdateMultiAccountUrl(args, controlledAccount ? JSON.parse(controlledAccount) : null);

  let result = await baseQuery(updatedArgs, api, extraOptions);
  const { logout, setCredentials } = authSlice.actions;

  if (result.error && result.error.status === 403) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult: QueryReturnValue = await baseQuery(
          {
            url: config.AUTHORIZATION.AUTH,
            method: 'POST',
            body: {
              grant_type: 'refresh_token',
              refresh_token: localStorage.getItem('refresh_token'),
            },
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          api.dispatch(
            setCredentials({
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              accessToken: refreshResult.data.access_token,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              refreshToken: refreshResult.data.refresh_token,
            }),
          );
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logout());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  /**
   * Дополнительная проверка помещена ниже так как иногда бывает после восстановлдение токена прилетает 401 status
   * что приводит к зацикливанию приложения
   */
  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
  }

  return result;
};

export const userApi = createApi({
  reducerPath: 'userAPI',
  tagTypes: [
    'UserInfo',
    'UserBanners',
    'RepairWorks',
    'Accounts',
    'RegularPayment',
    'PushSettings',
    'CardList',
    'ConnectedServices',
    'Preset',
    'History',
    'MultiAccount',
  ],
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});
